/**
 * offers.ts
 *
 * API layer for Offer CRUD + nested lifecycle/payment actions.
 *
 * Endpoints:
 *  GET    /offers/                              → list (perspective, status, page)
 *  POST   /offers/                              → create (save_as_draft)
 *  GET    /offers/{uuid}/                       → detail
 *  PATCH  /offers/{uuid}/                       → update
 *  POST   /offers/{uuid}/submissions/           → publish draft
 *  POST   /offers/{uuid}/acceptances/           → accept
 *  POST   /offers/{uuid}/declines/              → decline
 *  POST   /offers/{uuid}/change-requests/       → request changes
 *  POST   /offers/{uuid}/withdrawals/           → withdraw (creator)
 *  POST   /offers/{uuid}/payment-links/         → create Stripe payment link
 *  POST   /offers/{uuid}/off-platform-settlements/                       → record outside payment
 *  POST   /offers/{uuid}/off-platform-settlements/{id}/confirmations/   → counterparty confirms
 *  POST   /offers/{uuid}/off-platform-settlements/{id}/rejections/      → counterparty rejects
 *  POST   /offers/claims/                       → claim email-addressed offer
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api';
import { toast } from '../utils/toast';
import { extractApiError, parseApiError } from '../utils/apiError';
import { getValidAvatarUrl } from '../utils/avatar';
import type {
  CancellationPolicy,
  OfferChangeRequest,
  OfferItem,
  OfferMilestone,
  OfferPayment,
  OfferPaymentStatus,
  OfferStatus,
} from '../components/gigs/my-gigs/types';
import { isOfferEffectivelyExpired } from '../components/gigs/my-gigs/types';

// ─── API types ───────────────────────────────────────────────────────────────

export type OfferPerspective = 'created' | 'received';

export type OfferApiStatusFilter =
  | 'accepted'
  | 'pending'
  | 'sent'
  | 'draft'
  | 'rejected'
  | 'withdrawn'
  | 'expired'
  | 'changes_requested';

export interface ApiOfferMilestone {
  id?: number;
  label?: string;
  amount?: string | number;
  due?: string | null;
  percent?: string | number | null;
  payment_status?: string;
  position?: number;
}

export interface ApiOfferPayment {
  id?: number;
  transaction_id?: string;
  amount?: string | number;
  platform_fee_amount?: string | number;
  processing_fee_amount?: string | number;
  payer_total_amount?: string | number;
  provider_payout_amount?: string | number;
  payment_channel?: string | null;
  settlement_note?: string | null;
  status?: string;
  milestone_id?: number | null;
  milestone_ids?: number[] | null;
  stripe_payment_link_url?: string | null;
  proposed_by?: number | string | null;
  proposed_by_id?: number | string | null;
  author_id?: number | string | null;
  created_by?: number | string | null;
  created_at?: string;
}

export interface ApiOfferChangeRequest {
  id?: number;
  author_id?: number;
  requested_changes?: string;
  proposed_terms?: string;
  created_at?: string;
}

export interface ApiOfferParty {
  id?: number | string;
  user_id?: number | string;
  username?: string;
  full_name?: string;
  name?: string;
  email?: string;
  role?: string;
  avatar_url?: string;
  profile_image?: string;
  profile_picture?: string;
  image?: string;
  avatar?: string;
  prof_pics?: string;
  prof_pics_url?: string;
  google_prof_pics?: string;
}

export type ApiOfferPartyField = string | ApiOfferParty | null | undefined;

export interface ApiOfferListItem {
  offer_uuid: string;
  title?: string;
  price?: string | number;
  currency?: string;
  status?: string;
  recipient?: ApiOfferPartyField;
  creator?: ApiOfferPartyField;
  gig_uuid?: string | null;
  payment_status?: string;
  amount_paid?: string | number | null;
  remaining_balance?: string | number | null;
}

export interface ApiOfferDetail {
  offer_uuid: string;
  creator_id?: number;
  recipient_user_id?: number | null;
  recipient_email?: string | null;
  gig_uuid?: string | null;
  conversation_id?: string | null;
  predecessor_offer_uuid?: string | null;
  commercial_mode?: string | null;
  cancellation_policy?: string | null;
  decline_reason?: string | null;
  recipient_payouts_enabled?: boolean | null;
  recipient_can_receive_payments?: boolean | null;
  recipient_stripe_ready?: boolean | null;
  status?: string;
  title?: string;
  description?: string;
  deliverables?: string | string[] | null;
  currency?: string;
  price?: string | number;
  deposit?: string | number | null;
  milestones?: ApiOfferMilestone[];
  payments?: ApiOfferPayment[];
  change_requests?: ApiOfferChangeRequest[];
  start_date?: string | null;
  delivery_date?: string | null;
  revisions_included?: number;
  expiration_date?: string | null;
  timezone?: string | null;
  attachments?: unknown;
  internal_notes?: string | null;
  payment_status?: string;
  amount_paid?: string | number | null;
  remaining_balance?: string | number | null;
  stripe_payment_link_url?: string | null;
  sent_at?: string | null;
  accepted_at?: string | null;
  created_at?: string | null;
  recipient?: ApiOfferPartyField;
  creator?: ApiOfferPartyField;
}

export interface ApiOfferMilestonePayload {
  label: string;
  amount: string;
  due?: string | null;
  percent?: string | null;
}

export interface CreateOfferPayload {
  title?: string;
  description?: string;
  deliverables?: string;
  currency?: string;
  price?: string;
  deposit?: string | null;
  milestones?: ApiOfferMilestonePayload[];
  start_date?: string | null;
  delivery_date?: string | null;
  revisions_included?: number;
  expiration_date?: string | null;
  timezone?: string | null;
  attachments?: { name: string; url: string; file_size: number }[];
  internal_notes?: string | null;
  cancellation_policy?: CancellationPolicy | null;
  gig_uuid?: string | null;
  predecessor_offer_uuid?: string | null;
  conversation_id?: string | null;
  recipient_user_id?: number | null;
  recipient_email?: string | null;
  save_as_draft?: boolean;
}

export type UpdateOfferPayload = Omit<CreateOfferPayload, 'save_as_draft'> & {
  save_as_draft?: boolean;
};

export interface CreatePaymentLinkPayload {
  amount: string;
  milestone_ids?: number[];
  /** Deprecated by the API; converted to `milestone_ids` before sending. */
  milestone_id?: number | null;
}

export interface ProposeSettlementPayload {
  amount: string;
  note?: string;
}

export interface RequestOfferChangesPayload {
  requested_changes: string;
  proposed_terms?: string;
}

export interface OfferListParams {
  perspective?: OfferPerspective;
  status?: OfferApiStatusFilter;
  page?: number;
  page_size?: number;
}

export interface PaginatedOffersResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: OfferItem[];
}

export interface PaymentLinkResult {
  amount?: string;
  milestone_ids?: number[];
  milestone_id?: number | null;
  stripe_payment_link_url?: string | null;
  offer?: OfferItem;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function partyNumericId(party: ApiOfferPartyField): number | undefined {
  if (!party || typeof party !== 'object') return undefined;
  const raw = party.id ?? party.user_id;
  if (raw == null || raw === '') return undefined;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

function parseDecimal(value: unknown, fallback = 0): number {
  if (value == null || value === '') return fallback;
  const n = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
  return Number.isFinite(n) ? n : fallback;
}

function toDecimalString(value: number | string | undefined | null): string | undefined {
  if (value == null || value === '') return undefined;
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : undefined;
  return String(value);
}

function normalizeStatus(raw: unknown): OfferStatus {
  const s = String(raw || 'draft').toLowerCase();
  if (s === 'sent') return 'pending';
  if (s === 'declined') return 'rejected';
  if (
    s === 'in_negotiation' ||
    s === 'in-negotiation' ||
    s === 'negotiation' ||
    s === 'negotiating'
  )
    return 'changes_requested';
  if (s === 'change_requested' || s === 'changes-requested') return 'changes_requested';
  if (
    s === 'draft' ||
    s === 'pending' ||
    s === 'accepted' ||
    s === 'rejected' ||
    s === 'withdrawn' ||
    s === 'expired' ||
    s === 'changes_requested'
  ) {
    return s;
  }
  return 'draft';
}

function normalizeCancellationPolicy(raw: unknown): CancellationPolicy | null {
  const value = String(raw || '')
    .trim()
    .toLowerCase();
  if (value === 'flexible' || value === 'moderate' || value === 'strict') return value;
  return null;
}

function parseOptionalBoolean(value: unknown): boolean | null {
  if (value === true || value === 'true' || value === 1 || value === '1') return true;
  if (value === false || value === 'false' || value === 0 || value === '0') return false;
  return null;
}

function readRecipientPayoutReady(api: Record<string, unknown>): boolean | null {
  const directKeys = [
    'recipient_payouts_enabled',
    'recipient_can_receive_payments',
    'recipient_stripe_ready',
  ];
  for (const key of directKeys) {
    const parsed = parseOptionalBoolean(api[key]);
    if (parsed != null) return parsed;
  }
  const recipient = api.recipient;
  if (recipient && typeof recipient === 'object') {
    const party = recipient as Record<string, unknown>;
    for (const key of [
      'payouts_enabled',
      'can_receive_payments',
      'stripe_ready',
      'onboarding_completed',
    ]) {
      const parsed = parseOptionalBoolean(party[key]);
      if (parsed != null) return parsed;
    }
  }
  return null;
}

function normalizePaymentStatus(raw: unknown): OfferPaymentStatus {
  const s = String(raw || 'unpaid').toLowerCase();
  if (s === 'partially_paid' || s === 'paid' || s === 'failed' || s === 'unpaid') return s;
  return 'unpaid';
}

/** `asPicked` dates (start/due/delivery) are UTC-stamped as entered, so they are read back in UTC. */
function formatDateLabel(iso: string | null | undefined, asPicked = false): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(asPicked ? { timeZone: 'UTC' } : {}),
  });
}

function parseAttachmentFileSize(obj: Record<string, unknown>): number | undefined {
  const raw = obj.file_size ?? obj.size;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function parseAttachments(
  raw: unknown,
): { name: string; url: string; file_size?: number }[] | undefined {
  if (!raw) return undefined;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return undefined;
    try {
      return parseAttachments(JSON.parse(trimmed));
    } catch {
      return [{ name: 'Attachment', url: trimmed }];
    }
  }
  if (!Array.isArray(raw)) return undefined;
  return raw
    .map((item, idx) => {
      if (typeof item === 'string') return { name: `Attachment ${idx + 1}`, url: item };
      if (item && typeof item === 'object') {
        const obj = item as Record<string, unknown>;
        const url = String(obj.url ?? obj.secure_url ?? obj.href ?? '');
        if (!url) return null;
        const fileSize = parseAttachmentFileSize(obj);
        return {
          name: String(obj.name ?? obj.filename ?? `Attachment ${idx + 1}`),
          url,
          ...(fileSize != null ? { file_size: fileSize } : {}),
        };
      }
      return null;
    })
    .filter(Boolean) as { name: string; url: string; file_size?: number }[];
}

function mapMilestone(m: ApiOfferMilestone, index: number, totalPrice: number): OfferMilestone {
  const amount = parseDecimal(m.amount);
  const percent =
    m.percent != null && m.percent !== ''
      ? parseDecimal(m.percent)
      : totalPrice > 0
        ? (amount / totalPrice) * 100
        : undefined;
  const paymentStatus = normalizePaymentStatus(m.payment_status);
  return {
    id: String(m.id ?? index),
    title: m.label || `Milestone ${index + 1}`,
    amount,
    percentage: percent,
    dueDate: m.due ? formatDateLabel(m.due, true) : '',
    status: paymentStatus === 'paid' ? 'paid' : 'unpaid',
    paymentStatus,
    position: m.position,
  };
}

function mapMilestoneIds(p: ApiOfferPayment): number[] {
  if (Array.isArray(p.milestone_ids) && p.milestone_ids.length > 0) {
    return p.milestone_ids.map((id) => Number(id)).filter((id) => Number.isFinite(id));
  }
  if (p.milestone_id != null && Number.isFinite(Number(p.milestone_id))) {
    return [Number(p.milestone_id)];
  }
  return [];
}

function partyIdFrom(...values: (number | string | null | undefined)[]): number | null {
  for (const raw of values) {
    if (raw == null || raw === '') continue;
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function mapPayment(p: ApiOfferPayment): OfferPayment {
  const milestoneIds = mapMilestoneIds(p);
  const channel = p.payment_channel ? String(p.payment_channel).toLowerCase() : undefined;
  return {
    id: p.id ?? 0,
    transactionId: p.transaction_id,
    amount: parseDecimal(p.amount),
    platformFeeAmount:
      p.platform_fee_amount != null ? parseDecimal(p.platform_fee_amount) : undefined,
    processingFeeAmount:
      p.processing_fee_amount != null ? parseDecimal(p.processing_fee_amount) : undefined,
    payerTotalAmount: p.payer_total_amount != null ? parseDecimal(p.payer_total_amount) : undefined,
    providerPayoutAmount:
      p.provider_payout_amount != null ? parseDecimal(p.provider_payout_amount) : undefined,
    status: String(p.status || 'pending'),
    channel,
    note: p.settlement_note?.trim() || undefined,
    proposedById: partyIdFrom(p.proposed_by, p.proposed_by_id, p.author_id, p.created_by),
    milestoneId: milestoneIds[0] ?? null,
    milestoneIds,
    stripePaymentLinkUrl: p.stripe_payment_link_url ?? null,
    createdAt: p.created_at,
  };
}

function mapChangeRequest(cr: ApiOfferChangeRequest, index: number): OfferChangeRequest {
  return {
    id: cr.id ?? index,
    authorId: cr.author_id,
    requestedChanges: cr.requested_changes || undefined,
    proposedTerms: cr.proposed_terms || undefined,
    createdAt: cr.created_at,
  };
}

function unwrapOfferPayload(data: unknown): ApiOfferDetail | ApiOfferListItem {
  if (!data || typeof data !== 'object') return {} as ApiOfferDetail;
  const obj = data as Record<string, unknown>;
  if (obj.data && typeof obj.data === 'object' && !Array.isArray(obj.data)) {
    return obj.data as ApiOfferDetail;
  }
  return data as ApiOfferDetail;
}

function parseParty(party: ApiOfferPartyField): {
  label: string;
  username?: string;
  role?: string;
  avatar?: string;
  email?: string;
} {
  if (party == null || party === '') return { label: '' };
  if (typeof party === 'string') {
    const trimmed = party.trim();
    if (trimmed.includes('@')) return { label: trimmed, email: trimmed };
    return { label: trimmed, username: trimmed };
  }
  if (typeof party === 'object') {
    const username = party.username?.trim() || undefined;
    const fullName = (party.full_name || party.name)?.trim() || undefined;
    const email = party.email?.trim() || undefined;
    const role = party.role?.trim() || undefined;
    const avatar =
      getValidAvatarUrl(
        party.prof_pics ||
          party.avatar_url ||
          party.profile_image ||
          party.profile_picture ||
          party.image ||
          party.avatar,
        party.prof_pics_url || party.google_prof_pics,
      ) || undefined;
    const label = fullName || username || email || '';
    return { label, username, role, avatar, email };
  }
  return { label: '' };
}

export function mapApiOfferToItem(
  raw: ApiOfferDetail | ApiOfferListItem | Record<string, unknown>,
): OfferItem {
  const api = unwrapOfferPayload(raw) as ApiOfferDetail & ApiOfferListItem;
  const price = parseDecimal(api.price);
  const milestones = Array.isArray(api.milestones)
    ? api.milestones.map((m, i) => mapMilestone(m, i, price))
    : [];
  const payments = Array.isArray(api.payments) ? api.payments.map(mapPayment) : [];
  const changeRequests = Array.isArray(api.change_requests)
    ? api.change_requests.map((cr, i) => mapChangeRequest(cr, i))
    : [];
  const latestChangeRequest =
    changeRequests.length > 0 ? changeRequests[changeRequests.length - 1] : undefined;
  const recipient = parseParty(api.recipient);
  const creator = parseParty(api.creator);
  const recipientEmail =
    api.recipient_email ||
    recipient.email ||
    (recipient.label.includes('@') ? recipient.label : '') ||
    '';

  return {
    id: String(api.offer_uuid || ''),
    recipientName: recipient.label || recipientEmail || 'Recipient',
    recipientEmail,
    recipientUserId: api.recipient_user_id ?? partyNumericId(api.recipient) ?? null,
    recipientUsername: recipient.username,
    recipientAvatar: recipient.avatar,
    recipientRole: recipient.role,
    avatarInitial: (recipient.label || recipientEmail || 'R').charAt(0).toUpperCase(),
    creatorId: api.creator_id ?? partyNumericId(api.creator) ?? 0,
    creatorName: creator.label || undefined,
    creatorAvatar: creator.avatar,
    creatorRole: creator.role,
    gigUuid: api.gig_uuid ?? null,
    conversationId: api.conversation_id ?? null,
    predecessorOfferUuid: api.predecessor_offer_uuid ?? null,
    commercialMode: api.commercial_mode?.trim().toLowerCase() || null,
    cancellationPolicy: normalizeCancellationPolicy(api.cancellation_policy),
    declineReason: api.decline_reason?.trim() || undefined,
    recipientPayoutReady: readRecipientPayoutReady(api as unknown as Record<string, unknown>),
    title: api.title || 'Untitled offer',
    description: api.description || '',
    deliverables: api.deliverables || '',
    currency: api.currency || 'USD',
    price,
    deposit: api.deposit != null && api.deposit !== '' ? parseDecimal(api.deposit) : undefined,
    hasPaymentSchedule: milestones.length > 0,
    paymentType: milestones.length > 0 ? 'milestone' : 'single',
    milestones,
    payments,
    startDate: formatDateLabel(api.start_date, true) || '',
    deliveryDate: formatDateLabel(api.delivery_date, true) || undefined,
    revisionsIncluded: api.revisions_included ?? 0,
    expirationDate: api.expiration_date || undefined,
    timezone: api.timezone || undefined,
    attachments: parseAttachments(api.attachments),
    internalNotes: api.internal_notes || undefined,
    status: normalizeStatus(api.status),
    paymentStatus: normalizePaymentStatus(api.payment_status),
    amountPaid: api.amount_paid != null ? parseDecimal(api.amount_paid) : undefined,
    remainingBalance:
      api.remaining_balance != null ? parseDecimal(api.remaining_balance) : undefined,
    stripePaymentLinkUrl: api.stripe_payment_link_url ?? null,
    sentAt: api.sent_at || undefined,
    acceptedAt: api.accepted_at || undefined,
    createdAt: api.created_at ? formatDateLabel(api.created_at) : undefined,
    feedback: latestChangeRequest?.requestedChanges,
    changeRequests: changeRequests.length > 0 ? changeRequests : undefined,
  };
}

function toIsoDateTime(value: string | undefined | null): string | null | undefined {
  if (value == null || value === '') return value;
  // Date-only → start of day ISO
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return `${value}T00:00:00Z`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toISOString();
}

export function buildCreateOfferPayload(input: {
  title: string;
  description?: string;
  deliverables?: string;
  currency?: string;
  price?: number;
  deposit?: number | null;
  milestones?: { title: string; amount: number; percentage?: number; dueDate?: string }[];
  startDate?: string | null;
  deliveryDate?: string | null;
  revisionsIncluded?: number;
  expirationDate?: string | null;
  timezone?: string | null;
  attachments?: { name: string; url: string; file_size: number }[];
  internalNotes?: string | null;
  cancellationPolicy?: CancellationPolicy | null;
  gigUuid?: string | null;
  predecessorOfferUuid?: string | null;
  conversationId?: string | null;
  recipientUserId?: number | null;
  recipientEmail?: string | null;
  saveAsDraft?: boolean;
}): CreateOfferPayload {
  const payload: CreateOfferPayload = {
    save_as_draft: input.saveAsDraft ?? true,
  };

  const title = input.title.trim();
  if (title) payload.title = title;

  const description = input.description?.trim();
  if (description) payload.description = description;

  const deliverables = input.deliverables?.trim();
  if (deliverables) payload.deliverables = deliverables;

  if (input.currency) payload.currency = input.currency;

  if (input.price != null && Number.isFinite(input.price) && input.price !== 0) {
    payload.price = String(input.price);
  }

  if (input.deposit != null && Number.isFinite(input.deposit) && input.deposit !== 0) {
    payload.deposit = String(input.deposit);
  }

  if (input.milestones && input.milestones.length > 0) {
    payload.milestones = input.milestones.map((m) => {
      // Backend rejects `due: null`; omit the key when no date was entered.
      const due = toIsoDateTime(m.dueDate);
      return {
        label: m.title,
        amount: String(m.amount),
        ...(due ? { due } : {}),
        percent: m.percentage != null ? String(m.percentage) : null,
      };
    });
  }

  if (input.revisionsIncluded != null && Number.isFinite(input.revisionsIncluded)) {
    payload.revisions_included = input.revisionsIncluded;
  }

  if (input.attachments && input.attachments.length > 0) {
    payload.attachments = input.attachments.map((a) => {
      const fileSize =
        a.file_size != null && Number.isFinite(a.file_size) ? a.file_size : undefined;
      if (fileSize == null) {
        throw new Error(`Attachment "${a.name}" is missing file_size (bytes).`);
      }
      return {
        name: a.name,
        url: a.url,
        file_size: fileSize,
      };
    });
  }

  const internalNotes = input.internalNotes?.trim();
  if (internalNotes) payload.internal_notes = internalNotes;

  if (input.cancellationPolicy) payload.cancellation_policy = input.cancellationPolicy;

  if (input.startDate) payload.start_date = toIsoDateTime(input.startDate) ?? undefined;
  if (input.deliveryDate) payload.delivery_date = toIsoDateTime(input.deliveryDate) ?? undefined;
  if (input.expirationDate) {
    // Full ISO from zoned date+time, or date-only fallback via toIsoDateTime
    payload.expiration_date = /^\d{4}-\d{2}-\d{2}T/.test(input.expirationDate)
      ? input.expirationDate
      : (toIsoDateTime(input.expirationDate) ?? undefined);
  }
  if (input.timezone) payload.timezone = input.timezone;
  if (input.gigUuid) payload.gig_uuid = input.gigUuid;
  if (input.predecessorOfferUuid) payload.predecessor_offer_uuid = input.predecessorOfferUuid;
  if (input.conversationId) payload.conversation_id = input.conversationId;

  if (input.recipientUserId != null) {
    payload.recipient_user_id = input.recipientUserId;
  } else if (input.recipientEmail) {
    payload.recipient_email = input.recipientEmail;
  }

  return payload;
}

/**
 * PATCH body for an existing offer. The create builder omits empty fields,
 * which on PATCH would leave the old values in place, so every field the form
 * can clear is sent explicitly as empty.
 */
export function buildUpdateOfferPayload(
  input: Parameters<typeof buildCreateOfferPayload>[0],
): UpdateOfferPayload {
  const payload: UpdateOfferPayload = buildCreateOfferPayload(input);
  payload.description ??= '';
  payload.deliverables ??= '';
  payload.deposit ??= null;
  payload.milestones ??= [];
  payload.start_date ??= null;
  payload.delivery_date ??= null;
  payload.expiration_date ??= null;
  payload.attachments ??= [];
  payload.internal_notes ??= '';
  if (input.revisionsIncluded == null) payload.revisions_included = 0;
  return payload;
}

/** Fully paid accepted direct offers are finished work, not an open offer. */
function isSettledDirectOffer(offer: OfferItem): boolean {
  if (offer.gigUuid || offer.status !== 'accepted') return false;
  return (
    offer.paymentStatus === 'paid' ||
    (offer.remainingBalance != null && offer.remainingBalance <= 0.001)
  );
}

export function findConflictingActiveOffer(
  offers: OfferItem[],
  input: {
    gigUuid?: string | null;
    recipientUserId?: number | null;
    recipientEmail?: string | null;
    excludeOfferId?: string;
  },
): OfferItem | undefined {
  const email = input.recipientEmail?.trim().toLowerCase();
  return offers.find((offer) => {
    if (
      offer.status !== 'pending' &&
      offer.status !== 'changes_requested' &&
      offer.status !== 'accepted'
    ) {
      return false;
    }
    if (isOfferEffectivelyExpired(offer)) return false;
    if (isSettledDirectOffer(offer)) return false;
    if (input.excludeOfferId && offer.id === input.excludeOfferId) return false;
    if (input.gigUuid) {
      if (offer.gigUuid !== input.gigUuid) return false;
    } else if (offer.gigUuid) {
      return false;
    }
    if (input.recipientUserId != null && offer.recipientUserId != null) {
      return offer.recipientUserId === input.recipientUserId;
    }
    if (email && offer.recipientEmail) {
      return offer.recipientEmail.toLowerCase() === email;
    }
    return false;
  });
}

function extractPaymentLinkUrl(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const obj = data as Record<string, unknown>;
  const nested =
    obj.data && typeof obj.data === 'object' ? (obj.data as Record<string, unknown>) : null;
  const candidates = [
    obj.stripe_payment_link_url,
    nested?.stripe_payment_link_url,
    obj.url,
    nested?.url,
    obj.payment_link_url,
    nested?.payment_link_url,
  ];
  for (const c of candidates) {
    if (typeof c === 'string' && c.startsWith('http')) return c;
  }
  return null;
}

// ─── Queries ─────────────────────────────────────────────────────────────────

export const useOffersList = (
  params: OfferListParams = {},
  options: { enabled?: boolean } = {},
) => {
  return useQuery<PaginatedOffersResponse>({
    queryKey: ['offers', 'list', params],
    enabled: options.enabled !== false,
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params.perspective) searchParams.set('perspective', params.perspective);
      if (params.status) searchParams.set('status', params.status);
      if (params.page) searchParams.set('page', String(params.page));
      if (params.page_size) searchParams.set('page_size', String(params.page_size));
      const qs = searchParams.toString();
      const { data } = await apiClient.get(`/offers/${qs ? `?${qs}` : ''}`);
      type ApiListPayload = {
        count?: number;
        next?: string | null;
        previous?: string | null;
        results?: ApiOfferListItem[];
      };
      const payload = (data as { data?: ApiListPayload }).data ?? (data as ApiListPayload);
      const results = Array.isArray(payload.results)
        ? payload.results.map((r) => mapApiOfferToItem(r))
        : [];
      return {
        count: payload.count ?? results.length,
        next: payload.next ?? null,
        previous: payload.previous ?? null,
        results,
      };
    },
  });
};

export const useOffer = (
  offerUuid: string | undefined,
  options: {
    enabled?: boolean;
    refetchInterval?: number | false | ((offer: OfferItem | undefined) => number | false);
  } = {},
) => {
  const { refetchInterval } = options;
  return useQuery<OfferItem>({
    queryKey: ['offers', offerUuid],
    enabled: !!offerUuid && options.enabled !== false,
    refetchInterval:
      typeof refetchInterval === 'function'
        ? (query) => refetchInterval(query.state.data)
        : refetchInterval,
    queryFn: async () => {
      const { data } = await apiClient.get(`/offers/${offerUuid}/`);
      return mapApiOfferToItem(data as ApiOfferDetail);
    },
  });
};

// ─── Mutations ───────────────────────────────────────────────────────────────

function invalidateOffers(queryClient: ReturnType<typeof useQueryClient>, offerUuid?: string) {
  queryClient.invalidateQueries({ queryKey: ['offers', 'list'] });
  if (offerUuid) queryClient.invalidateQueries({ queryKey: ['offers', offerUuid] });
}

type OfferResponseAction = 'accept' | 'decline' | 'changes' | 'withdraw';

/**
 * A 400/409 on an offer action usually means the offer moved on (expired,
 * accepted, withdrawn) since the page loaded. Refetch it so the UI catches up
 * and explain the real reason instead of a generic failure.
 */
async function reportOfferActionError(
  queryClient: ReturnType<typeof useQueryClient>,
  offerUuid: string,
  action: OfferResponseAction,
  err: unknown,
  fallback: string,
) {
  invalidateOffers(queryClient, offerUuid);
  const status = parseApiError(err).status;
  if (status === 400 || status === 409) {
    try {
      const { data } = await apiClient.get(`/offers/${offerUuid}/`);
      const offer = mapApiOfferToItem(data as ApiOfferDetail);
      queryClient.setQueryData(['offers', offerUuid], offer);
      const current = isOfferEffectivelyExpired(offer) ? 'expired' : offer.status;
      if (current === 'expired') {
        toast.error('This offer is no longer available.');
        return;
      }
      if (current === 'withdrawn') {
        toast.error('This offer has already been withdrawn.');
        return;
      }
      if (current === 'accepted') {
        toast.error(
          action === 'withdraw'
            ? 'This offer has already been accepted and cannot be withdrawn.'
            : 'This offer has already been accepted.',
        );
        return;
      }
      if (current === 'rejected') {
        toast.error('This offer has already been declined.');
        return;
      }
    } catch {
      // fall through to the API message
    }
  }
  toast.error(extractApiError(err, fallback));
}

export const useCreateOffer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateOfferPayload) => {
      const { data } = await apiClient.post('/offers/', payload);
      return mapApiOfferToItem(data as ApiOfferDetail);
    },
    onSuccess: (offer) => {
      toast.success(offer.status === 'draft' ? 'Offer saved as draft.' : 'Offer sent successfully');
      invalidateOffers(queryClient, offer.id);
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Unable to save offer. Please try again.'));
    },
  });
};

export const useUpdateOffer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      offerUuid,
      payload,
    }: {
      offerUuid: string;
      payload: UpdateOfferPayload;
      /** Skip the success toast (e.g. when a send follows and toasts on its own). */
      silent?: boolean;
    }) => {
      const { data } = await apiClient.patch(`/offers/${offerUuid}/`, payload);
      return mapApiOfferToItem(data as ApiOfferDetail);
    },
    onSuccess: (offer, { silent }) => {
      if (!silent) toast.success('Offer updated successfully');
      invalidateOffers(queryClient, offer.id);
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to update offer'));
    },
  });
};

export const useSubmitOffer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (offerUuid: string) => {
      const { data } = await apiClient.post(`/offers/${offerUuid}/submissions/`, {});
      return mapApiOfferToItem(data as ApiOfferDetail);
    },
    onSuccess: (offer) => {
      toast.success('Offer sent successfully');
      invalidateOffers(queryClient, offer.id);
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to send offer'));
    },
  });
};

export const useAcceptOffer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (offerUuid: string) => {
      const { data } = await apiClient.post(`/offers/${offerUuid}/acceptances/`, {});
      return mapApiOfferToItem(data as ApiOfferDetail);
    },
    onSuccess: (offer) => {
      toast.success('Offer accepted.');
      invalidateOffers(queryClient, offer.id);
    },
    onError: (err: unknown, offerUuid) => {
      void reportOfferActionError(
        queryClient,
        offerUuid,
        'accept',
        err,
        'Unable to accept offer. Please try again.',
      );
    },
  });
};

export const useDeclineOffer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ offerUuid, reason }: { offerUuid: string; reason: string }) => {
      const { data } = await apiClient.post(`/offers/${offerUuid}/declines/`, { reason });
      return mapApiOfferToItem(data as ApiOfferDetail);
    },
    onSuccess: (offer) => {
      toast.info('Offer has been declined.');
      invalidateOffers(queryClient, offer.id);
    },
    onError: (err: unknown, { offerUuid }) => {
      void reportOfferActionError(
        queryClient,
        offerUuid,
        'decline',
        err,
        'Unable to decline offer. Please try again.',
      );
    },
  });
};

export const useRequestOfferChanges = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      offerUuid,
      payload,
    }: {
      offerUuid: string;
      payload: RequestOfferChangesPayload;
    }) => {
      const body: RequestOfferChangesPayload = {
        requested_changes: payload.requested_changes,
      };
      if (payload.proposed_terms?.trim()) {
        body.proposed_terms = payload.proposed_terms.trim();
      }
      const { data } = await apiClient.post(`/offers/${offerUuid}/change-requests/`, body);
      return mapApiOfferToItem(data as ApiOfferDetail);
    },
    onSuccess: (offer) => {
      toast.success('Your change request has been sent.');
      invalidateOffers(queryClient, offer.id);
    },
    onError: (err: unknown, { offerUuid }) => {
      void reportOfferActionError(
        queryClient,
        offerUuid,
        'changes',
        err,
        'Unable to submit change request. Please try again.',
      );
    },
  });
};

export const useWithdrawOffer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (offerUuid: string) => {
      const { data } = await apiClient.post(`/offers/${offerUuid}/withdrawals/`, {});
      return mapApiOfferToItem(data as ApiOfferDetail);
    },
    onSuccess: (offer) => {
      toast.info('Offer withdrawn.');
      invalidateOffers(queryClient, offer.id);
    },
    onError: (err: unknown, offerUuid) => {
      void reportOfferActionError(
        queryClient,
        offerUuid,
        'withdraw',
        err,
        'Unable to withdraw offer. Please try again.',
      );
    },
  });
};

export const useCreateOfferPaymentLink = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      offerUuid,
      payload,
    }: {
      offerUuid: string;
      payload: CreatePaymentLinkPayload;
    }) => {
      const milestoneIds =
        payload.milestone_ids ?? (payload.milestone_id != null ? [payload.milestone_id] : []);
      const body: { amount: string; milestone_ids?: number[] } = { amount: payload.amount };
      if (milestoneIds.length > 0) body.milestone_ids = milestoneIds;
      const { data } = await apiClient.post(`/offers/${offerUuid}/payment-links/`, body);
      const url = extractPaymentLinkUrl(data);
      let offer: OfferItem | undefined;
      const mapped = mapApiOfferToItem(data as ApiOfferDetail);
      if (mapped.id) offer = mapped;

      // If response doesn't include a URL, refetch detail for stripe_payment_link_url / payment rows
      let stripeUrl = url || offer?.stripePaymentLinkUrl || null;
      if (!stripeUrl) {
        const { data: detail } = await apiClient.get(`/offers/${offerUuid}/`);
        offer = mapApiOfferToItem(detail as ApiOfferDetail);
        stripeUrl =
          offer.payments?.find((p) => p.stripePaymentLinkUrl && p.status.toLowerCase() !== 'paid')
            ?.stripePaymentLinkUrl ||
          offer.stripePaymentLinkUrl ||
          offer.payments?.find((p) => p.stripePaymentLinkUrl)?.stripePaymentLinkUrl ||
          null;
      }

      return {
        amount: toDecimalString(payload.amount),
        milestone_ids: milestoneIds,
        milestone_id: milestoneIds[0] ?? null,
        stripe_payment_link_url: stripeUrl,
        offer,
      } satisfies PaymentLinkResult;
    },
    onSuccess: (result, { offerUuid }) => {
      if (result.offer?.id) queryClient.setQueryData(['offers', offerUuid], result.offer);
      invalidateOffers(queryClient, offerUuid);
      queryClient.invalidateQueries({ queryKey: ['offer-payments'] });
    },
    onError: (err: unknown, { offerUuid }) => {
      invalidateOffers(queryClient, offerUuid);
      const message = extractApiError(err, '');
      if (/at least|minimum/i.test(message)) {
        toast.error('Payments must be at least $10.00.');
        return;
      }
      if (/stripe|payout|onboard|payment setup|connect/i.test(message)) {
        toast.error('Complete payment setup to continue.');
        return;
      }
      if (/fee/i.test(message)) {
        toast.error("We couldn't calculate the payment fees. Please try again.");
        return;
      }
      toast.error(
        message ||
          "We couldn't process this payment. Please review the payment details and try again.",
      );
    },
  });
};

function onSettlementSuccess(
  queryClient: ReturnType<typeof useQueryClient>,
  offerUuid: string,
  offer: OfferItem,
) {
  if (offer.id) queryClient.setQueryData(['offers', offerUuid], offer);
  invalidateOffers(queryClient, offerUuid);
  queryClient.invalidateQueries({ queryKey: ['offer-payments'] });
}

function settlementErrorMessage(err: unknown, fallback: string): string {
  const message = extractApiError(err, '');
  if (/at least|minimum/i.test(message)) return 'Payments must be at least $10.00.';
  if (/match/i.test(message)) {
    return "The amount doesn't match the recorded payment. Please refresh and try again.";
  }
  return message || fallback;
}

export const useProposeOffPlatformSettlement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      offerUuid,
      payload,
    }: {
      offerUuid: string;
      payload: ProposeSettlementPayload;
    }) => {
      const body: ProposeSettlementPayload = { amount: payload.amount };
      if (payload.note?.trim()) body.note = payload.note.trim();
      const { data } = await apiClient.post(`/offers/${offerUuid}/off-platform-settlements/`, body);
      return mapApiOfferToItem(data as ApiOfferDetail);
    },
    onSuccess: (offer, { offerUuid }) => {
      toast.success('Payment recorded. Waiting for the other party to confirm.');
      onSettlementSuccess(queryClient, offerUuid, offer);
    },
    onError: (err: unknown, { offerUuid }) => {
      invalidateOffers(queryClient, offerUuid);
      toast.error(
        settlementErrorMessage(err, "We couldn't record this payment. Please try again."),
      );
    },
  });
};

export const useConfirmOffPlatformSettlement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      offerUuid,
      paymentId,
      amount,
    }: {
      offerUuid: string;
      paymentId: number;
      amount: string;
    }) => {
      const { data } = await apiClient.post(
        `/offers/${offerUuid}/off-platform-settlements/${paymentId}/confirmations/`,
        { amount },
      );
      return mapApiOfferToItem(data as ApiOfferDetail);
    },
    onSuccess: (offer, { offerUuid }) => {
      toast.success('Payment confirmed.');
      onSettlementSuccess(queryClient, offerUuid, offer);
    },
    onError: (err: unknown, { offerUuid }) => {
      invalidateOffers(queryClient, offerUuid);
      toast.error(
        settlementErrorMessage(err, "We couldn't confirm this payment. Please try again."),
      );
    },
  });
};

export const useRejectOffPlatformSettlement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ offerUuid, paymentId }: { offerUuid: string; paymentId: number }) => {
      const { data } = await apiClient.post(
        `/offers/${offerUuid}/off-platform-settlements/${paymentId}/rejections/`,
        {},
      );
      return mapApiOfferToItem(data as ApiOfferDetail);
    },
    onSuccess: (offer, { offerUuid }) => {
      toast.info('Payment rejected.');
      onSettlementSuccess(queryClient, offerUuid, offer);
    },
    onError: (err: unknown, { offerUuid }) => {
      invalidateOffers(queryClient, offerUuid);
      toast.error(
        settlementErrorMessage(err, "We couldn't reject this payment. Please try again."),
      );
    },
  });
};

export const useClaimOffer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post('/offers/claims/', {});
      return mapApiOfferToItem(data as ApiOfferDetail);
    },
    onSuccess: (offer) => {
      toast.success('Offer claimed successfully');
      invalidateOffers(queryClient, offer.id || undefined);
    },
    onError: () => {
      // Auto-claim often 400s when already claimed — keep quiet
    },
  });
};
